import React, { createContext, useReducer } from 'react';
import PropTypes from 'prop-types';

import { APP_LIST_WITH_PROXY, SEARCH_APPLICATION } from 'reducers/actions';
import MainReducer, { initialState } from 'reducers/main';
import useAuth from 'hooks/useAuth';

const MainContext = createContext(null);

export const MainProvider = ({ children }) => {
  const [state, dispatch] = useReducer(MainReducer, initialState);

  const { axiosServices } = useAuth();

  const getAppList = async () => {
    try {
      const response = await axiosServices.get('/app_list', { withCredentials: true });
      dispatch({
        type: APP_LIST_WITH_PROXY,
        payload: { appList: response.data.appList }
      });
      return response.data.appList;
    } catch (error) {
      return { status: false, message: error.response?.data?.message || error.message };
    }
  };

  const checkHealth = async (serverSelection) => {
    try {
      const response = await axiosServices.post('/check-seocromom-health', { serverSelection });
      return response.data.healthStatuses;
    } catch (error) {
      return null;
    }
  };

  const runApp = async (id, url, proxyServer) => {
    try {
      const response = await axiosServices.post('/run_app', { id, url, proxyServer }, { withCredentials: true });
      return { status: true, message: response.data.freePort ?? response.data.port };
    } catch (error) {
      return { status: false, message: error.response?.data?.message || error.message };
    }
  };

  const stopApp = async (id) => {
    try {
      await axiosServices.post('/stop_app', { id }, { withCredentials: true });
      return { status: true, message: '' };
    } catch (error) {
      return { status: false, message: error.response?.data?.message || error.message };
    }
  };

  const setSearchPattern = (searchPattern) => {
    dispatch({
      type: SEARCH_APPLICATION,
      payload: { searchPattern }
    });
  };

  const setLog = async (id) => {
    try {
      const response = await axiosServices.post('/logs', { id });
      dispatch({
        type: APP_LIST_WITH_PROXY,
        payload: { appList: response.data.appList }
      });
    } catch (error) {
      return {
        status: false,
        message: error.response?.data?.message || error.message
      };
    }
  };

  const setFavorite = async (id) => {
    try {
      const response = await axiosServices.post('/favorites', { id });
      dispatch({
        type: APP_LIST_WITH_PROXY,
        payload: { appList: response.data.appList }
      });
    } catch (error) {
      return {
        status: false,
        message: error.response?.data?.message || error.message
      };
    }
  };

  // Request-a-tool submission. Backend attaches the user's email + plan from the
  // session and forwards an Intercom conversation; we only send the form fields.
  const postToolRequest = async ({ toolName, link, useCase }) => {
    try {
      const response = await axiosServices.post('/tool_request', {
        tool_name: toolName,
        link,
        use_case: useCase
      });
      return { status: true, message: response.data?.message || 'Tool request submitted.' };
    } catch (error) {
      return {
        status: false,
        message: error.response?.data?.message || error.message || 'Failed to submit tool request.'
      };
    }
  };

  // Remote feature flags (admin-controlled). Fails OPEN so a flag-fetch outage
  // never hides a working feature.
  const getFeatureFlags = async () => {
    try {
      const response = await axiosServices.get('/feature_flags');
      return response.data?.feature_flags || {};
    } catch (error) {
      return { request_tool_enabled: true };
    }
  };

  // What's New: admin-authored announcements. One call returns the entries, the
  // sidebar unread count, the Tools banner (or null) and the app ids that should
  // carry a NEW badge.
  const getWhatsNew = async () => {
    try {
      const response = await axiosServices.get('/whats_new');
      return { status: true, data: response.data || {} };
    } catch (error) {
      return { status: false, message: error.response?.data?.message || error.message || "Failed to load What's New." };
    }
  };

  const markWhatsNewSeen = async () => {
    try {
      await axiosServices.post('/whats_new/seen', {});
      return { status: true };
    } catch (error) {
      return { status: false, message: error.response?.data?.message || error.message };
    }
  };

  const dismissWhatsNewBanner = async (batch) => {
    try {
      await axiosServices.post('/whats_new/banner_dismiss', { batch });
      return { status: true };
    } catch (error) {
      return { status: false, message: error.response?.data?.message || error.message };
    }
  };

  return (
    <MainContext.Provider
      value={{
        ...state,
        getAppList,
        checkHealth,
        runApp,
        stopApp,
        setSearchPattern,
        setLog,
        setFavorite,
        postToolRequest,
        getFeatureFlags,
        getWhatsNew,
        markWhatsNewSeen,
        dismissWhatsNewBanner
      }}
    >
      {children}
    </MainContext.Provider>
  );
};

MainProvider.propTypes = {
  children: PropTypes.node
};

export default MainContext;
